import { isNil } from 'lodash';
import {
  Box,
  Radio,
  Switch,
  Checkbox,
  FormGroup,
  FormLabel,
  TextField,
  RadioGroup,
  RadioProps,
  Typography,
  SelectProps,
  FormControl,
  SwitchProps,
  CheckboxProps,
  TextFieldProps,
  FormHelperText,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';
import {
  Controller,
  FieldValues,
  RegisterOptions,
  useFormContext,
} from 'react-hook-form';
import {
  useBuildingsShortQuery,
  useICD10ListQuery,
  usePatientsShortQuery,
  usePracticeShortQuery,
  useProvidersShortQuery,
  useRespitePartnersShortQuery,
  useTimeOffUserListShortQuery,
  useUserListShortQuery,
} from '@common/cache';
import { useEffect, useState } from 'react';
import {
  DatePicker,
  LocalizationProvider,
  DatePickerProps,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { FormGooglePlacesAutocomplete } from 'components/GooglePlacesAutocomplete';
import { extractAddressComponents } from '@util/google-places';
import { statesUS } from '@util/regions';
import { MultiselectField } from 'components/Multiselect/Multiselect';
import { Select } from 'ui';
import { SearchField } from 'components/SearchField';
import { StatusBanner } from '../StatusBaner';
import { RepeaterTextField } from 'components/RepeaterTextField';
import { parseConditional } from '@util/dynamicFormParseConditional.ts';
import { USStateSelect } from 'components/USStateSelect';
import { RepeaterWrapper } from 'components/RepeaterFields';

interface IDynamicFormFieldProps {
  name: string;
  label?: string;
  disabled?: boolean;
  disable_if?: string;
  max_date?: string;
  min_date?: string;
  optional?: boolean;
  placeholder?: string;
  onChangeCallback?: (nameList: string[]) => void;
  record_id?: string;
  clearable?: boolean;
  helperText?: string;
  rules?:
    | Omit<
        RegisterOptions<FieldValues, string>,
        'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
      >
    | undefined;
  none_options?: string[];
  hide_if?: string;
  highlight?: 'None' | 'Warning' | 'Bold' | 'Italics' | 'Underline';
  disable_past?: boolean;
  disable_future?: boolean;
  min?: number;
  max?: number;
  unit?: string;
  helper_text?: string;
}

interface IOptionFieldProps {
  options?: { value: string; label: string }[];
}

export const TextFormField = ({
  name,
  label,
  disabled = false,
  disable_if,
  optional,
  onChangeCallback,
  ...props
}: IDynamicFormFieldProps & TextFieldProps) => {
  const { control, watch, formState, resetField } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  useEffect(() => {
    if (isDisabled && formState.touchedFields[name]) {
      resetField(name);
    }
  }, [isDisabled]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          type='text'
          size='medium'
          label={label}
          variant='outlined'
          error={!isNil(error)}
          helperText={error ? error.message : props.helper_text}
          required={!optional}
          onChange={event => {
            if (isDisabled) return;
            field.onChange(event.target.value);
            if (onChangeCallback) onChangeCallback([name]);
          }}
          fullWidth
          {...props}
          value={field.value ?? ''}
        />
      )}
    />
  );
};

export const TextAreaFormField = ({
  name,
  label,
  disabled = false,
  disable_if,
  optional,
  onChangeCallback,
  charLimit,
  slotProps,
  ...props
}: IDynamicFormFieldProps & TextFieldProps & { charLimit?: number }) => {
  const { control, watch, formState, resetField } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  useEffect(() => {
    if (isDisabled && formState.touchedFields[name]) {
      resetField(name);
    }
  }, [isDisabled]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          type='text'
          label={label}
          variant='outlined'
          error={!isNil(error)}
          required={!optional}
          onChange={event => {
            if (isDisabled) return;
            field.onChange(event);
            if (onChangeCallback) onChangeCallback([name]);
          }}
          fullWidth
          multiline
          minRows={3}
          helperText={
            error
              ? error.message
              : charLimit &&
                `${field.value ? field.value.length + `/${charLimit}` : ''}`
          }
          slotProps={{
            formHelperText: {
              style: {
                textAlign: error ? 'left' : 'right',
              },
            },
            ...slotProps,
          }}
          {...props}
        />
      )}
    />
  );
};

interface INumberFieldProps {
  float?: boolean;
}

export const NumberInputFormField = ({
  name,
  label,
  disabled = false,
  disable_if,
  optional,
  onChangeCallback,
  float = false,
  max,
  unit,
  ...props
}: IDynamicFormFieldProps & TextFieldProps & INumberFieldProps) => {
  const { control, watch, setError, clearErrors } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <TextField
          {...field}
          value={field.value === undefined ? '' : field.value}
          label={label}
          variant='outlined'
          type='number'
          error={!isNil(error)}
          helperText={error ? error.message : null}
          required={!optional}
          onKeyDown={evt => {
            const allowedKeys = [
              'Backspace',
              'ArrowUp',
              'ArrowDown',
              'ArrowLeft',
              'ArrowRight',
              'Tab',
              'Delete',
            ];

            if (
              !/^[0-9]$/.test(evt.key) &&
              !(float && evt.key === '.') &&
              !allowedKeys.includes(evt.key) &&
              !(evt.ctrlKey || evt.metaKey)
            ) {
              evt.preventDefault();
            }
          }}
          onChange={event => {
            if (isDisabled) return;

            const raw = event.target.value ?? '';
            const cleaned = float
              ? raw.replace(/[^\d.]/g, '').replace(/(\..*)\./g, '$1')
              : raw.replace(/\D/g, '');

            field.onChange(cleaned);
            onChangeCallback?.([name]);

            if (max !== undefined && Number(cleaned) > max) {
              setError(name, {
                type: 'max',
                message: `Minutes must be smaller or equal to ${max}`,
              });
            } else {
              clearErrors(name);
            }
          }}
          fullWidth
          slotProps={{
            input:
              float && unit
                ? {
                    endAdornment: (
                      <InputAdornment position='end'>{unit}</InputAdornment>
                    ),
                  }
                : undefined,
          }}
          {...props}
        />
      )}
    />
  );
};

export const CheckboxFormField = ({
  name,
  label,
  disabled = false,
  disable_if,
  onChangeCallback,
  optional,
  ...props
}: IDynamicFormFieldProps & CheckboxProps) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <FormGroup>
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              required={!optional}
              control={
                <Checkbox
                  {...field}
                  required={!optional}
                  checked={field.value?.toString() === 'true'}
                  sx={{ py: 0 }}
                  onChange={() => {
                    if (isDisabled) return;
                    field.onChange(
                      field.value.toString() === 'false' ? 'true' : 'false',
                    );
                    if (onChangeCallback) onChangeCallback([name]);
                  }}
                  {...props}
                />
              }
              label={label}
            />
          </FormGroup>
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const ToggleFormField = ({
  name,
  label,
  disabled = false,
  disable_if,
  onChangeCallback,
  optional,
  ...props
}: IDynamicFormFieldProps & SwitchProps) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <FormGroup>
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              required={!optional}
              control={
                <Switch
                  {...field}
                  checked={field.value.toString() === 'true'}
                  onChange={() => {
                    if (isDisabled) return;
                    field.onChange(
                      field.value.toString() === 'false' ? 'true' : 'false',
                    );
                    if (onChangeCallback) onChangeCallback([name]);
                  }}
                  {...props}
                  color={props.highlight === 'Warning' ? 'error' : 'primary'}
                />
              }
              label={label}
            />
          </FormGroup>
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const RadioButtonFormField = ({
  name,
  label,
  options = [],
  row = false,
  disabled = false,
  disable_if,
  optional,
  onChangeCallback,
  ...props
}: IDynamicFormFieldProps &
  IOptionFieldProps &
  RadioProps & { row?: boolean }) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl>
          {label && (
            <FormLabel sx={{ width: '100%' }} required={!optional} id={name}>
              {label}
            </FormLabel>
          )}
          {options.length > 0 && (
            <RadioGroup row={row} defaultValue={options[0]} {...field}>
              {options.map(option => (
                <FormControlLabel
                  id={option.value}
                  key={option.value}
                  value={option.value}
                  onClick={() => {
                    if (isDisabled) return;
                    field.onChange(
                      option.value === field.value ? '' : option.value,
                    );
                    if (onChangeCallback) onChangeCallback([name]);
                  }}
                  control={<Radio disabled={isDisabled} {...props} />}
                  label={option.label}
                  htmlFor={option.value}
                />
              ))}
            </RadioGroup>
          )}
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const SelectFormField = ({
  name,
  label,
  options = [],
  disabled = false,
  optional,
  disable_if,
  onChangeCallback,
  helperText,
  rules = {},
}: IDynamicFormFieldProps & IOptionFieldProps) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ ...rules }}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <Select
          {...field}
          clearable={optional}
          options={options}
          value={field.value ?? ''}
          label={label ? label : undefined}
          error={!isNil(error)}
          required={!optional && !!label}
          onChange={event => {
            if (isDisabled) return;
            field.onChange(event);
            if (onChangeCallback) onChangeCallback([name]);
          }}
          disabled={isDisabled}
          clearLabel='-'
          helperText={error?.message ?? helperText}
        />
      )}
    />
  );
};

export const USStateSelectFormField = ({
  name,
  label,
  disabled = false,
  optional,
  disable_if,
  onChangeCallback,
  helperText,
  rules = {},
}: IDynamicFormFieldProps & IOptionFieldProps) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      rules={{ ...rules }}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <USStateSelect
          {...field}
          clearable={optional}
          value={field.value ?? ''}
          label={label ? label : undefined}
          error={!isNil(error)}
          required={!optional && !!label}
          onChange={event => {
            if (isDisabled) return;
            field.onChange(event);
            if (onChangeCallback) onChangeCallback([name]);
          }}
          disabled={isDisabled}
          clearLabel='-'
          helperText={error?.message ?? helperText}
        />
      )}
    />
  );
};

export const MultiselectFormField = ({
  name,
  label,
  options = [],
  disabled = false,
  optional,
  disable_if,
  onChangeCallback,
  none_options = [],
}: IDynamicFormFieldProps & IOptionFieldProps & SelectProps) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => {
        const parsedValue = field.value ? JSON.parse(field.value) : [];

        const handleChange = (newValue: string[]) => {
          field.onChange(JSON.stringify(newValue));
          if (onChangeCallback) onChangeCallback([name]);
        };

        return (
          <FormControl fullWidth>
            <MultiselectField
              value={parsedValue}
              handleChange={handleChange}
              name={name}
              label={label}
              options={options}
              disabled={isDisabled}
              optional={optional}
              error={!isNil(error)}
              disableAllOption
              placeholder={''}
              none_options={none_options}
            />
            <FormHelperText error={!isNil(error)}>
              {error ? error.message : null}
            </FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};

export const RepeaterTextFormField = ({
  name,
  label,
  disabled = false,
  optional,
  disable_if,
  onChangeCallback,
  multiline,
}: IDynamicFormFieldProps &
  IOptionFieldProps &
  TextFieldProps & { multiline?: boolean }) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => {
        const parsedValue = field.value ? JSON.parse(field.value) : [];

        const handleChange = (newValue: unknown[]) => {
          field.onChange(JSON.stringify(newValue));
          if (onChangeCallback) onChangeCallback([name]);
        };

        return (
          <FormControl fullWidth>
            <RepeaterTextField
              value={parsedValue}
              handleChange={handleChange}
              name={name}
              label={label}
              disabled={isDisabled}
              optional={optional}
              error={!isNil(error)}
              multiline={multiline}
            />
            <FormHelperText error={!isNil(error)}>
              {error ? error.message : null}
            </FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};

export const CheckboxListFormField = ({
  name,
  label,
  options = [],
  disabled = false,
  optional,
  disable_if,
  onChangeCallback,
  none_options = [],
}: IDynamicFormFieldProps & IOptionFieldProps) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => {
        const selectedOptions = field.value ? JSON.parse(field.value) : [];
        const isNoneSelected = (selectedOptions as string[]).some(option =>
          none_options.includes(option),
        );
        const isOtherThanNoneSelected = (selectedOptions as string[]).some(
          option => option && !none_options.includes(option),
        );

        const handleChange = (optionId: string | number) => {
          let newSelected;
          if (selectedOptions.includes(optionId)) {
            newSelected = selectedOptions.filter(
              (val: string) => val !== optionId,
            );
          } else {
            newSelected = [...selectedOptions, optionId];
          }
          field.onChange(JSON.stringify(newSelected));
          if (onChangeCallback) onChangeCallback([name]);
        };

        return (
          <FormControl fullWidth error={!isNil(error)}>
            {label && <FormLabel required={!optional}>{label}</FormLabel>}
            <FormGroup>
              {options.map((option, index) => (
                <FormControlLabel
                  key={`${name}-${option.value}-${index}`}
                  control={
                    <Checkbox
                      checked={selectedOptions.includes(option.value)}
                      onChange={() => handleChange(option.value)}
                      disabled={
                        isDisabled ||
                        (none_options.includes(option.value) &&
                          isOtherThanNoneSelected) ||
                        (!selectedOptions.includes(option.value) &&
                          isNoneSelected)
                      }
                    />
                  }
                  label={option.label}
                />
              ))}
            </FormGroup>
            <FormHelperText>{error ? error.message : null}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
};

export const BuildingSearchFormField = ({
  name,
  disabled = false,
  disable_if,
  optional,
  placeholder = 'Search a building',
  label,
  onChangeCallback,
  ...props
}: IDynamicFormFieldProps) => {
  const { control, getValues } = useFormContext();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const formValues = getValues();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value);
    }
  };

  const { data: options = [] } = useBuildingsShortQuery({ search });

  const getFieldValue = (id: string) =>
    options.find(option => option.id === id);
  const formValue = getValues(name);

  useEffect(() => {
    if (!formValue) setSearch(undefined);
  }, [formValue]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            name={name}
            placeholder={placeholder}
            options={options}
            onSearchChange={newInputValue => {
              handleSearchChange(newInputValue);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;
              field.onChange(newValue ?? '');
              if (onChangeCallback) onChangeCallback([name]);
            }}
            value={getFieldValue(field.value)?.id ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            error={!isNil(error)}
            {...props}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const ProviderSearchFormField = ({
  name,
  disabled = false,
  disable_if,
  optional,
  placeholder = 'Search a provider',
  label,
  onChangeCallback,
  record_id,
  ...props
}: IDynamicFormFieldProps) => {
  const { control, getValues } = useFormContext();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const formValues = getValues();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value);
    }
  };

  const { data: options = [] } = useProvidersShortQuery({ search, record_id });

  const getFieldValue = (id: string) =>
    options.find(option => option.id === id);
  const formValue = getValues(name);

  useEffect(() => {
    if (!formValue) setSearch(undefined);
  }, [formValue]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            name={name}
            placeholder={placeholder}
            options={options.map(option => ({
              ...option,
              name: option?.inactive
                ? `${option.name} (Inactive)`
                : option.name,
            }))}
            onSearchChange={newInputValue => {
              handleSearchChange(newInputValue);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;
              field.onChange(newValue ?? '');
              if (onChangeCallback) onChangeCallback([name]);
            }}
            value={getFieldValue(field.value)?.id ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            error={!isNil(error)}
            {...props}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const RespitePartnersSearchFormField = ({
  name,
  disabled = false,
  disable_if,
  patient_id,
  label,
  optional,
  onChangeCallback,
  placeholder = 'Search a respite partner',
  ...props
}: IDynamicFormFieldProps & { patient_id: string }) => {
  const { control, getValues } = useFormContext();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const formValues = getValues();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value);
    }
  };

  const { data: options = [] } = useRespitePartnersShortQuery({
    search,
    patient_id,
  });

  const getFieldValue = (id: string) =>
    options.find(option => option.id === id);
  const formValue = getValues(name);

  useEffect(() => {
    if (!formValue) setSearch(undefined);
  }, [formValue]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            name={name}
            placeholder={placeholder}
            options={options}
            onSearchChange={newInputValue => {
              handleSearchChange(newInputValue);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;
              field.onChange(newValue ?? '');
              if (onChangeCallback) onChangeCallback([name]);
            }}
            value={getFieldValue(field.value)?.id ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            error={!isNil(error)}
            {...props}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const PatientsSearchFormField = ({
  name,
  disabled = false,
  disable_if,
  placeholder = 'Search a patient',
  label,
  optional,
  searchQuery = '',
  onChangeCallback,
  ...props
}: IDynamicFormFieldProps & { searchQuery?: string }) => {
  const { control, getValues } = useFormContext();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const formValues = getValues();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const noOptionsText =
    search && search.length > 2
      ? t('common.autocompletePlaceholder.noOptions')
      : t('common.autocompletePlaceholder.enterThreeLetter');

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value);
    }
  };

  const { data: options = [] } = usePatientsShortQuery({
    search: search || searchQuery,
  });

  const getFieldValue = (id: string) =>
    options.find(option => option.id === id);
  const formValue = getValues(name);

  useEffect(() => {
    if (!formValue) setSearch(undefined);
  }, [formValue]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            name={name}
            placeholder={placeholder}
            options={options}
            onSearchChange={newInputValue => {
              handleSearchChange(newInputValue);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;
              field.onChange(newValue ?? '');
              if (onChangeCallback) onChangeCallback([name]);
            }}
            value={getFieldValue(field.value)?.id ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            noOptionsText={noOptionsText}
            error={!isNil(error)}
            {...props}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const UserSearchFormField = ({
  name,
  disabled = false,
  disable_if,
  placeholder = 'Search a user',
  label,
  optional,
  record_id,
  onChangeCallback,
  role = '',
  ...props
}: IDynamicFormFieldProps & { role?: string }) => {
  const { control, getValues } = useFormContext();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const formValues = getValues();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const { data: options = [] } = useUserListShortQuery({
    search,
    record_id,
    ...(role !== '' && { role }),
  });

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value);
    }
  };

  const getFieldValue = (id: string) =>
    options.find(option => option.id === id);
  const formValue = getValues(name);

  useEffect(() => {
    if (!formValue) setSearch(undefined);
  }, [formValue]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            name={name}
            placeholder={placeholder}
            options={options}
            onSearchChange={newInputValue => {
              handleSearchChange(newInputValue);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;
              field.onChange(newValue ?? '');
              if (onChangeCallback) onChangeCallback([name]);
            }}
            value={getFieldValue(field.value)?.id ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            error={!isNil(error)}
            {...props}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const TimeOffUserSearchFormField = ({
  name,
  disabled = false,
  disable_if,
  placeholder = 'Search a user',
  label,
  optional,
  onChangeCallback,
  role = '',
  ...props
}: IDynamicFormFieldProps & { role?: string }) => {
  const { control, getValues } = useFormContext();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const formValues = getValues();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const { data: options = [] } = useTimeOffUserListShortQuery({
    search,
    ...(role !== '' && { role }),
  });

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value);
    }
  };

  const getFieldValue = (id: string) =>
    options.find(option => option.id === id);
  const formValue = getValues(name);

  useEffect(() => {
    if (!formValue) setSearch(undefined);
  }, [formValue]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            name={name}
            placeholder={placeholder}
            options={options}
            onSearchChange={newInputValue => {
              handleSearchChange(newInputValue);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;
              field.onChange(newValue ?? '');
              if (onChangeCallback) onChangeCallback([name]);
            }}
            value={getFieldValue(field.value)?.id ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            error={!isNil(error)}
            {...props}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const PracticeSearchFormField = ({
  name,
  disabled = false,
  disable_if,
  label,
  placeholder = 'Search a practice',
  record_id,
  onChangeCallback,
  optional,
  ...props
}: IDynamicFormFieldProps) => {
  const { control, getValues } = useFormContext();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const formValues = getValues();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const { data: options = [] } = usePracticeShortQuery({ record_id, search });

  const handleSearchChange = (value: string) => {
    if (value !== search) {
      setSearch(value);
    }
  };

  const getFieldValue = (id: string) =>
    options.find(option => option.id === id) || null;

  const formValue = getValues(name);

  useEffect(() => {
    if (!formValue) setSearch(undefined);
  }, [formValue]);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            name={name}
            placeholder={placeholder}
            options={options}
            onSearchChange={newInputValue => {
              handleSearchChange(newInputValue);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;
              field.onChange(newValue ?? '');
              if (onChangeCallback) onChangeCallback([name]);
            }}
            value={getFieldValue(field.value)?.id ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            error={!isNil(error)}
            {...props}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};

export const ICD10SearchFormField = ({
  name,
  disabled = false,
  disable_if,
  label,
  placeholder = 'ICD-10 Code',
  onChangeCallback,
  optional,
  ...props
}: IDynamicFormFieldProps) => {
  const { control, getValues } = useFormContext();
  const formValues = getValues();
  const formValue = getValues(name) as string | undefined;

  const [search, setSearch] = useState<string | undefined>(undefined);
  const [resolvedOption, setResolvedOption] = useState<{
    id: string;
    name: string;
    inactive?: boolean;
  } | null>(null);

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const effectiveSearch =
    search ?? (formValue && !resolvedOption ? formValue : undefined);

  const { data: options = [] } = useICD10ListQuery({ search: effectiveSearch });

  useEffect(() => {
    if (!formValue) {
      setResolvedOption(null);
      return;
    }

    const match = options.find(option => option.id === formValue);
    if (match) {
      setResolvedOption(match);
    }
  }, [formValue, options]);

  const mergedOptions =
    resolvedOption && !options.some(option => option.id === resolvedOption.id)
      ? [resolvedOption, ...options]
      : options;

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => (
        <FormControl fullWidth>
          <SearchField
            {...field}
            {...props}
            name={name}
            placeholder={placeholder}
            options={mergedOptions}
            onSearchChange={newInputValue => {
              setSearch(newInputValue || undefined);
            }}
            onValueChange={newValue => {
              if (isDisabled) return;

              field.onChange(newValue ?? '');
              setSearch(undefined);

              const selected = mergedOptions.find(
                option => option.id === newValue,
              );
              setResolvedOption(selected ?? null);

              onChangeCallback?.([name]);
            }}
            value={field.value ?? ''}
            search={search}
            disabled={isDisabled}
            label={label}
            optional={optional}
            error={!isNil(error)}
          />
          <FormHelperText error={!isNil(error)}>
            {error ? error.message : null}
          </FormHelperText>
        </FormControl>
      )}
    />
  );
};
export const RepeaterICD10FormField = ({
  name,
  disabled = false,
  disable_if,
  label,
  onChangeCallback,
  optional,
  max,
  min,
  ...props
}: IDynamicFormFieldProps) => {
  return (
    <RepeaterWrapper
      name={name}
      disabled={disabled}
      maxNumberOfEntries={max}
      minNumberOfEntries={min}
      onChangeCallback={onChangeCallback}
      renderField={indexNumber => (
        <ICD10SearchFormField
          name={`${name}.${indexNumber}`}
          disabled={disabled}
          disable_if={disable_if}
          placeholder={label}
          onChangeCallback={onChangeCallback}
          optional={optional}
          {...props}
        />
      )}
    />
  );
};
export const DatepickerFormField = ({
  name,
  label,
  disabled = false,
  clearable = true,
  disable_if,
  optional,
  onChangeCallback,
  helperText = '',
  disable_future = false,
  disable_past = false,
  max_date,
  min_date,
  ...props
}: IDynamicFormFieldProps & DatePickerProps) => {
  const { control, watch } = useFormContext();
  const formValues = watch();

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  return (
    <Controller
      name={name}
      control={control}
      disabled={isDisabled}
      render={({ field, fieldState: { error } }) => {
        const value = field.value ? dayjs(field.value) : null;

        return (
          <FormControl fullWidth>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='en'>
              <DatePicker
                disabled={isDisabled}
                label={`${label} ${optional ? '' : '*'}`}
                disableFuture={disable_future}
                maxDate={max_date ? dayjs(max_date) : undefined}
                minDate={min_date ? dayjs(min_date) : undefined}
                disablePast={disable_past}
                value={value}
                onChange={val => {
                  if (isDisabled) return;
                  if (max_date && val && dayjs(val).isAfter(dayjs(max_date)))
                    return;

                  if (disable_future && val && dayjs(val).isAfter(dayjs()))
                    return;

                  if (
                    disable_past &&
                    val &&
                    dayjs(val).add(1, 'day').isBefore(dayjs())
                  )
                    return;

                  const formattedValue =
                    val && dayjs(val).isValid()
                      ? dayjs(val).format('YYYY-MM-DD')
                      : '';

                  field.onChange(formattedValue);
                  onChangeCallback?.([name]);
                }}
                slotProps={{
                  field: { clearable },
                  textField: {
                    error: !!error,
                    helperText: error ? error.message : helperText,
                  },
                }}
                {...props}
              />
            </LocalizationProvider>
          </FormControl>
        );
      }}
    />
  );
};

export const StaticLabelField = ({
  name,
  label,
  highlight,
}: IDynamicFormFieldProps) => {
  const { watch } = useFormContext();
  const fieldValue = watch(name);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Typography
        variant='body1'
        sx={{
          fontWeight: highlight === 'Bold' ? 'bold' : 'normal',
          fontStyle: highlight === 'Italics' ? 'italic' : 'normal',
          textDecoration: highlight === 'Underline' ? 'underline' : 'none',
          color: highlight === 'Warning' ? 'error' : 'text.secondary',
        }}
      >
        {label ? `${label}:` : ''}
      </Typography>
      <Typography variant='body1' sx={{ ml: 1, color: 'text.primary' }}>
        {fieldValue}
      </Typography>
    </Box>
  );
};

export const BannerField = ({
  name,
  label,
  variant,
}: IDynamicFormFieldProps & { variant: 'warning' | 'info' }) => {
  const { watch } = useFormContext();
  const fieldValue = watch(name);
  const text = label || fieldValue;

  return (
    text && (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <StatusBanner variant={variant} text={text} />
      </Box>
    )
  );
};

//IMPORTANT: only use this in Dynamic forms
export const GooglePlacesAutocompleteDynamicFormField = ({
  name,
  disabled = false,
  disable_if,
  label,
  onChangeCallback,
  optional,
}: IDynamicFormFieldProps) => {
  const { getValues, setValue, trigger } = useFormContext();
  const formValues = getValues();

  const match = name.match(/^(.*)_google_places_id$/);
  const prefix = match ? match[1] : undefined;

  if (prefix === undefined) return <></>;

  const isDisabled = disabled || parseConditional(disable_if, formValues);

  const handleSelectLocation = (place: Partial<google.maps.places.Place>) => {
    const { streetName, streetNumber, county, city, state, zipCode, country } =
      extractAddressComponents(place);
    const fieldNamesArray = [];

    if (place.id) {
      const fieldName = `${prefix}_google_places_id`;
      if (fieldName in formValues) {
        setValue(fieldName, place.id);
        fieldNamesArray.push(fieldName);
      }
    }

    if (streetName) {
      const fullStreet = `${streetNumber ? streetNumber + ' ' : ''}${streetName}`;
      const fieldName = `${prefix}_line`;
      if (fieldName in formValues) {
        setValue(fieldName, fullStreet);
        fieldNamesArray.push(fieldName);
      }
    }

    if (city) {
      const fieldName = `${prefix}_city`;
      if (fieldName in formValues) {
        setValue(fieldName, city);
        fieldNamesArray.push(fieldName);
      }
    }

    if (county) {
      const fieldName = `${prefix}_county`;

      if (fieldName in formValues) {
        setValue(fieldName, county);
        fieldNamesArray.push(fieldName);
      }
    }

    if (country) {
      const fieldName = `${prefix}_country`;
      if (fieldName in formValues) {
        setValue(fieldName, country);
        fieldNamesArray.push(fieldName);
      }
    }

    if (state && statesUS.includes(state)) {
      const fieldName = `${prefix}_state`;
      if (fieldName in formValues) {
        setValue(fieldName, state);
        fieldNamesArray.push(fieldName);
      }
    }

    if (zipCode) {
      const fieldName = `${prefix}_postal_code`;
      if (fieldName in formValues) {
        setValue(fieldName, zipCode);
        fieldNamesArray.push(fieldName);
      }
    }

    if (onChangeCallback) onChangeCallback(fieldNamesArray);
    trigger();
  };

  return (
    <FormGooglePlacesAutocomplete
      name={name}
      label={label}
      disabled={isDisabled}
      onChange={handleSelectLocation}
      required={!optional}
    />
  );
};
